<template>
    <v-dialog
        :value="isOpen"
        scrollable
        max-width="1100px"
        transition="slide-y-transition"
        eager
        persistent
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <ConfirmDialog
            :is-open="deleteAttachmentDialog"
            :action-loading="actionLoading.deleteAttachment"
            removal
            @on-confirm="confirmDeleteAttachment"
            @on-abort="closeDeleteAttachmentDialog"
        >
            <template #title>
                Usuwanie załącznika
            </template>
            <template #content>
                Czy na pewno chcesz usunąć załącznik: <strong>{{ currentAttachment ? getAttachmentName(currentAttachment) : '' }}</strong>?
            </template>
            <template #confirmButton>
                Usuń
            </template>
        </ConfirmDialog>
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    Awizacja od klienta
                </h1>
            </v-card-title>
            <v-card-text>
                <v-form ref="form">
                    <v-row>
                        <v-col cols="6">
                            <v-text-field
                                v-model="formData.supplier"
                                label="Dostawca"
                                outlined
                                dense
                                :maxlength="160"
                                counter
                                hide-details
                            />
                        </v-col>
                        <v-col cols="6">
                            <v-datetime-picker
                                v-model="estimatedPaperDeliveryDate"
                                label="Planowana data dostawy"
                                date-format="dd.MM.yyyy"
                                :date-picker-props="datetimeProps.dateProps"
                                :text-field-props="datetimeProps.textFieldProps"
                                :time-picker-props="datetimeProps.timeProps"
                                @input="estimatedPaperDeliveryDateHandler"
                            >
                                <template slot="dateIcon">
                                    <v-icon>mdi-calendar</v-icon>
                                </template>
                                <template slot="timeIcon">
                                    <v-icon>mdi-clock-outline</v-icon>
                                </template>
                                <template slot="actions" slot-scope="{parent}">
                                    <v-btn text color="error" @click.native="parent.clearHandler">
                                        Wyczyść
                                    </v-btn>
                                    <v-btn text color="success" @click="parent.okHandler">
                                        Zapisz
                                    </v-btn>
                                </template>
                            </v-datetime-picker>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="4">
                            <v-text-field
                                v-model="formData.paperType"
                                label="Rodzaj papieru"
                                outlined
                                dense
                                :maxlength="160"
                                counter
                                hide-details
                            />
                        </v-col>
                        <v-col cols="4">
                            <v-text-field
                                v-model="formData.paperWeight"
                                label="Waga papieru"
                                outlined
                                dense
                                :maxlength="80"
                                counter
                                hide-details
                            />
                        </v-col>
                        <v-col cols="4">
                            <v-text-field
                                v-model.number="$v.formData.numberOfPallets.$model"
                                label="Liczba palet"
                                outlined
                                dense
                                type="number"
                                :min="1"
                                :error-messages="numberOfPalletsErrors"
                            />
                        </v-col>
                    </v-row>
                    <v-divider />
                    <v-row>
                        <v-col cols="8">
                            <v-file-input
                                v-model="currentFiles"
                                label="Wybierz pliki"
                                multiple
                                counter
                                prepend-icon="mdi-paperclip"
                                outlined
                                dense
                                hide-details
                                :show-size="1000"
                                :accept="acceptedFileTypes"
                                :disabled="attachments.length >= 3 || actionLoading.upload"
                            >
                                <template #selection="{text}">
                                    <v-chip label small>
                                        {{ text }}
                                    </v-chip>
                                </template>
                            </v-file-input>
                        </v-col>
                        <v-col cols="auto">
                            <v-btn
                                color="primary"
                                outlined
                                :disabled="!currentFiles || currentFiles.length === 0 || attachments.length >= 3 || actionLoading.upload"
                                data-sentry-label="Dodaj plik awizacji"
                                @click="uploadFiles"
                            >
                                <v-icon left>
                                    mdi-file-upload
                                </v-icon>
                                Dodaj
                            </v-btn>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="8" class="pb-0">
                            <v-data-table
                                v-if="attachments.length > 0"
                                :headers="attachmentHeaders"
                                :items="attachments"
                                :mobile-breakpoint="0"
                                disable-pagination
                                hide-default-footer
                                class="elevation-0"
                            >
                                <template #item.fileName="{item}">
                                    <v-tooltip top :open-delay="tooltipDelay" :close-delay="tooltipDelay">
                                        <template #activator="{on, attrs}">
                                            <div
                                                v-bind="attrs"
                                                class="pointer d-flex align-center"
                                                style="height: 100%; width: 100%; padding: 8px 0;"
                                                v-on="on"
                                                @click="openAttachment(item)"
                                            >
                                                {{ getAttachmentName(item) }}
                                            </div>
                                        </template>
                                        <span>Kliknij żeby pobrać</span>
                                    </v-tooltip>
                                </template>
                                <template #item.actions="{item}">
                                    <v-tooltip top>
                                        <template #activator="{on}">
                                            <v-btn
                                                icon
                                                small
                                                v-on="on"
                                                @click="showDeleteAttachmentDialog(item)"
                                            >
                                                <v-icon color="error">
                                                    mdi-delete
                                                </v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Usuń</span>
                                    </v-tooltip>
                                </template>
                            </v-data-table>
                            <v-alert
                                v-else
                                type="info"
                                text
                                dense
                                border="left"
                                class="mb-0"
                            >
                                Nie dodano załączników.
                            </v-alert>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="8">
                            <v-alert
                                type="info"
                                text
                                dense
                                border="left"
                            >
                                Dopuszczalny rozmiar pojedynczego pliku: <strong>{{ maxFileSizeMB }} MB</strong>
                            </v-alert>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>
            <v-divider />
            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    data-sentry-label="Anuluj"
                    :disabled="actionLoading.save || actionLoading.upload"
                    @click="onClose"
                >
                    Anuluj
                </v-btn>
                <v-spacer />
                <v-btn
                    color="success"
                    text
                    data-sentry-label="Zapisz"
                    :loading="actionLoading.save"
                    :disabled="actionLoading.upload || $v.formData.numberOfPallets.$invalid"
                    @click="onSave"
                >
                    Zapisz
                </v-btn>
            </v-card-actions>
        </v-card>

        <v-overlay
            color="white"
            opacity="0.85"
            absolute
            :value="fileProgress.show"
        >
            <v-container class="pa-0">
                <v-row justify="center">
                    <v-col cols="auto">
                        <v-progress-circular
                            :rotate="-90"
                            :size="120"
                            :width="4"
                            :value="fileProgress.value"
                            color="primary"
                        >
                            {{ fileProgress.value }}
                        </v-progress-circular>
                    </v-col>
                </v-row>
                <v-row justify="center">
                    <span class="text-caption primary--text">{{ fileProgress.fileName }}</span>
                </v-row>
                <v-row justify="center">
                    <v-col cols="auto">
                        <v-btn
                            color="error"
                            small
                            outlined
                            data-sentry-label="Anuluj"
                            @click="abortUploadAttachment(fileProgress.fileName)"
                        >
                            Anuluj
                        </v-btn>
                    </v-col>
                </v-row>
            </v-container>
        </v-overlay>
    </v-dialog>
</template>

<script>
import ConfirmDialog from '@/components/common/ConfirmDialog'
import { appConfig, apiConfig } from '@/config'
import tooltipDelay from '@/mixins/tooltip'
import { minValue } from 'vuelidate/lib/validators'

export default {
    name: 'AdviceNoteDialog',
    components: {
        ConfirmDialog
    },
    mixins: [tooltipDelay],
    props: {
        isOpen: {
            type: Boolean,
        },
        deferSave: {
            type: Boolean,
            default: false
        },
        orderId: {
            type: Number,
            default: null
        },
        jobId: {
            type: Number,
            default: null
        },
        adviceNote: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            formData: {
                supplier: null,
                estimatedPaperDeliveryDate: null,
                paperType: null,
                paperWeight: null,
                numberOfPallets: null
            },
            estimatedPaperDeliveryDate: null,
            datetimeProps: {
                textFieldProps: {
                    outlined: true,
                    dense: true,
                    prependIcon: 'mdi-calendar'
                },
                dateProps: {
                    firstDayOfWeek: 1
                },
                timeProps: {
                    format: '24hr',
                    scrollable: true
                }
            },
            currentFiles: [],
            attachments: [],
            validationError: null,
            deleteAttachmentDialog: false,
            currentAttachment: null,
            acceptedFileTypes: '.jpg,.jpeg,.png,.pdf',
            actionLoading: {
                save: false,
                upload: false,
                deleteAttachment: false
            },
            fileProgress: {
                show: false,
                value: 0,
                fileName: null
            },
            attachmentHeaders: [
                {
                    text: 'Nazwa pliku',
                    value: 'fileName',
                    align: 'left',
                    sortable: false
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    align: 'right',
                    sortable: false,
                    width: '80px'
                }
            ]
        }
    },
    computed: {
        totalFilesSize() {
            return this.attachments.reduce((total, attachment) => total + attachment.size, 0)
        },
        
        maxFileSizeMB() {
            return appConfig.adviceNotes.adviceNoteAttachmentSize
        },
        numberOfPalletsErrors() {
            const errors = []
            
            if (this.$v.formData.numberOfPallets.$dirty) {
                if (!this.$v.formData.numberOfPallets.minValue) {
                    errors.push('Liczba palet musi być większa lub równa 1')
                }
            }
            
            return errors
        }
    },
    validations() {
        return {
            formData: {
                numberOfPallets: {
                    minValue: (value) => {
                        if (value === null || value === undefined || value === '') {
                            return true // Optional field
                        }

                        return minValue(1)(value)
                    }
                }
            }
        }
    },
    watch: {
        isOpen(newVal) {
            if (newVal) {
                this.loadAdviceNote()
            } else {
                this.resetForm()
            }
        },
        adviceNote: {
            immediate: true,
            handler(newVal) {
                if (newVal && this.isOpen) {
                    this.loadAdviceNote()
                }
            }
        }
    },
    methods: {
        async loadAdviceNote() {
            if (this.adviceNote && this.adviceNote.id) {
                const response = await this.$store.dispatch('adviceNotes/getAdviceNoteById', {
                    adviceNoteId: this.adviceNote.id
                })
                
                this.populateFormData(response.data)
            } else if (this.adviceNote) {
                this.populateFormData(this.adviceNote)
            } else {
                this.resetForm()
            }
        },
        populateFormData(adviceNoteData) {
            if (!adviceNoteData) {
                return
            }
            
            this.formData = {
                supplier: adviceNoteData.supplier || null,
                estimatedPaperDeliveryDate: adviceNoteData.estimatedPaperDeliveryDate || null,
                paperType: adviceNoteData.paperType || null,
                paperWeight: adviceNoteData.paperWeight || null,
                numberOfPallets: adviceNoteData.numberOfPallets || null
            }
            
            this.estimatedPaperDeliveryDate = adviceNoteData.estimatedPaperDeliveryDate ? new Date(adviceNoteData.estimatedPaperDeliveryDate) : null
            
            const attachments = adviceNoteData.attachments || []

            this.attachments = attachments.length > 0 ? attachments : []
        },
        estimatedPaperDeliveryDateHandler(date) {
            this.estimatedPaperDeliveryDate = date
            if (date) {
                this.formData.estimatedPaperDeliveryDate = date.toISOString()
            } else {
                this.formData.estimatedPaperDeliveryDate = null
            }
        },

        async uploadFiles() {
            if (!this.currentFiles || this.currentFiles.length === 0) {
                return
            }

            const files = Array.isArray(this.currentFiles) ? this.currentFiles : [this.currentFiles]

            await this.uploadFilesToServer(files)

            this.currentFiles = []
        },
        async uploadFilesToServer(files) {
            for (const file of files) {
                if (this.attachments.length >= 3) {
                    break
                }

                await this.uploadSingleFile(file)
            }
        },
        async uploadSingleFile(file) {
            const formData = new FormData()

            formData.append('file', file)

            if (this.adviceNote && this.adviceNote.id) {
                formData.append('adviceNote', this.adviceNote.id)
            }

            this.actionLoading.upload = true
            this.fileProgress.show = true
            this.fileProgress.fileName = file.name
            this.fileProgress.value = 0

            try {
                const response = await this.$store.dispatch('adviceNotes/createAdviceNoteAttachment', {
                    formData,
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)

                        this.fileProgress.value = percentCompleted
                    }
                })

                if (response && response.data) {
                    this.attachments.push(response.data)
                }
            } finally {
                this.actionLoading.upload = false
                this.fileProgress.show = false
                this.fileProgress.value = 0
                this.fileProgress.fileName = null
            }
        },
        showDeleteAttachmentDialog(attachment) {
            this.currentAttachment = attachment
            this.deleteAttachmentDialog = true
        },
        closeDeleteAttachmentDialog() {
            this.deleteAttachmentDialog = false
            this.currentAttachment = null
        },
        async confirmDeleteAttachment() {
            if (!this.currentAttachment) {
                this.closeDeleteAttachmentDialog()

                return
            }

            this.actionLoading.deleteAttachment = true

            try {
                if (this.currentAttachment.id) {
                    await this.$store.dispatch('adviceNotes/deleteAdviceNoteAttachment', {
                        attachmentId: this.currentAttachment.id
                    })
                }

                this.attachments = this.attachments.filter(a => {
                    if (a.id) {
                        return a.id !== this.currentAttachment.id
                    }

                    return a.name !== this.currentAttachment.name
                })

                this.closeDeleteAttachmentDialog()
            } catch (error) {
                // Error handling is done in store action
            } finally {
                this.actionLoading.deleteAttachment = false
            }
        },
        getAttachmentName(attachment) {
            if (!attachment) {
                return 'Nieznany plik'
            }

            if (attachment.file) {
                const parts = attachment.file.split('/')

                return parts[parts.length - 1]
            }

            if (attachment.name) {
                return attachment.name
            }

            return 'Nieznany plik'
        },
        openAttachment(attachment) {
            if (!attachment) {
                return
            }

            if (!attachment.file) {
                return
            }

            const mediaRoot = apiConfig.FACTORYBASE_API_URI
            const fileUrl = attachment.file.startsWith('http') 
                ? attachment.file 
                : `${mediaRoot}${attachment.file}`

            window.open(fileUrl, '_blank').focus()
        },
        async onSave() {   
            this.actionLoading.save = true
            
            try {
                const draftPayload = {
                    supplier: this.formData.supplier || null,
                    estimatedPaperDeliveryDate: this.formData.estimatedPaperDeliveryDate,
                    paperType: this.formData.paperType || null,
                    paperWeight: this.formData.paperWeight || null,
                    numberOfPallets: this.formData.numberOfPallets || null,
                    attachments: this.attachments,
                    attachmentIds: this.attachments.filter(a => a.id).map(a => a.id)
                }

                if (this.deferSave) {
                    this.$emit('on-save', draftPayload)
                    this.onClose()

                    return
                }

                const payload = {
                    orderId: this.orderId,
                    jobId: this.jobId,
                    adviceNoteId: this.adviceNote && this.adviceNote.id ? this.adviceNote.id : null,
                    supplier: draftPayload.supplier,
                    estimatedPaperDeliveryDate: draftPayload.estimatedPaperDeliveryDate,
                    paperType: draftPayload.paperType,
                    paperWeight: draftPayload.paperWeight,
                    numberOfPallets: draftPayload.numberOfPallets,
                    attachmentIds: draftPayload.attachmentIds
                }
            
                const response = await this.$store.dispatch('adviceNotes/saveAdviceNote', payload)

                if (response && response.data && response.data.id && !this.adviceNote?.id) {
                    this.$set(this, 'adviceNote', { ...this.adviceNote, id: response.data.id })
                }
                
                this.$emit('on-save', response.data)
                this.onClose()
            } catch (error) {
                // Error handling is done in store action
            } finally {
                this.actionLoading.save = false
            }
        },
        onClose() {
            this.$emit('on-close')
        },
        resetForm() {
            this.formData = {
                supplier: null,
                estimatedPaperDeliveryDate: null,
                paperType: null,
                paperWeight: null,
                numberOfPallets: null
            }
            this.estimatedPaperDeliveryDate = null
            this.currentFiles = []
            this.attachments = []
            this.validationError = null
        }
    },
}
</script>
